/**
 * features/hr/ComplianceCenter.tsx
 * ------------------------------------------------------------------
 * HR DOMAIN — Compliance.
 *
 * School-wide roll-up plus "documents needing attention" queue. Per
 * staff drill-down is in StaffProfile (Compliance tab); this screen
 * is the bird's-eye view. Clicking any staff row navigates to the
 * profile via onOpenStaff.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import {
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  FileWarning,
  Search,
} from 'lucide-react';
import { GradientHero, SoftCard, StatChip, glassFieldClass } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { useHrData } from '@/features/hr/useHrData';
import StaffWorkspace from '@/features/hr/StaffWorkspace';
import {
  summariseStaffCompliance,
  docHealth,
  daysUntil,
  staffFullName,
  staffInitials,
  DOC_TYPE_LABELS,
  STAFF_CATEGORY_LABELS,
  type HrStaff,
  type HrComplianceDoc,
  type DocHealth,
} from '@/lib/hr';

const HEALTH_BADGE: Record<DocHealth, { label: string; cls: string }> = {
  ok: {
    label: 'Compliant',
    cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
  expiring: {
    label: 'Renew soon',
    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  expired: {
    label: 'Expired',
    cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },
  missing: {
    label: 'Missing',
    cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },
};

function expiryNote(doc: HrComplianceDoc): string {
  const d = daysUntil(doc.expiryDate);
  if (d === null) return 'No expiry';
  if (d < 0) return `expired ${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} ago`;
  if (d === 0) return 'expires today';
  return `in ${d} day${d === 1 ? '' : 's'}`;
}

interface Props {
  onOpenStaff: (staffId: string) => void;
}

export default function ComplianceCenter({ onOpenStaff }: Props) {
  const data = useHrData();
  const [search, setSearch] = useState('');

  const summaries = useMemo(() => {
    return data.staff.map((s) =>
      summariseStaffCompliance(s, data.complianceDocs, data.complianceMatrix),
    );
  }, [data.staff, data.complianceDocs, data.complianceMatrix]);

  const totals = useMemo(() => {
    let fullyCompliant = 0;
    let expiring = 0;
    let expired = 0;
    let missing = 0;
    for (const sum of summaries) {
      if (sum.worst === 'ok') fullyCompliant += 1;
      expiring += sum.expiring;
      expired += sum.expired;
      missing += sum.missingMandatory;
    }
    return { fullyCompliant, expiring, expired, missing };
  }, [summaries]);

  const attention = useMemo(() => {
    const rows: { doc: HrComplianceDoc; staff: HrStaff; health: DocHealth }[] = [];
    for (const doc of data.complianceDocs) {
      const h = docHealth(doc);
      if (h === 'ok') continue;
      const staff = data.staffById(doc.staffId);
      if (!staff) continue;
      rows.push({ doc, staff, health: h });
    }
    const rank: Record<DocHealth, number> = {
      expired: 0,
      missing: 1,
      expiring: 2,
      ok: 3,
    };
    return rows.sort((a, b) => {
      if (rank[a.health] !== rank[b.health]) return rank[a.health] - rank[b.health];
      return (daysUntil(a.doc.expiryDate) ?? 0) - (daysUntil(b.doc.expiryDate) ?? 0);
    });
  }, [data.complianceDocs, data.staffById]);

  const filteredStaff = useMemo(() => {
    const q = search.trim().toLowerCase();
    const withSum = data.staff.map((s) => ({
      staff: s,
      sum: summaries.find((x) => x.staffId === s.id)!,
    }));
    const matched = q
      ? withSum.filter((r) => staffFullName(r.staff).toLowerCase().includes(q))
      : withSum;
    const rank: Record<DocHealth, number> = {
      expired: 0,
      missing: 1,
      expiring: 2,
      ok: 3,
    };
    return matched.sort((a, b) => rank[a.sum.worst] - rank[b.sum.worst]);
  }, [data.staff, summaries, search]);

  return (
    <div className="space-y-6">
      <GradientHero
        icon={ShieldCheck}
        title="Staff compliance"
        subtitle="School-wide roll-up — one board for Ministry inspections"
      />

      {data.loading ? (
        <div className="flex justify-center py-16">
          <Spinner label="Loading compliance data…" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatChip tone="emerald" icon={CheckCircle2} value={totals.fullyCompliant} label="Fully compliant" />
            <StatChip tone="gold" icon={AlertTriangle} value={totals.expiring} label="Expiring soon" />
            <StatChip tone="red" icon={XCircle} value={totals.expired} label="Expired" />
            <StatChip tone="red" icon={FileWarning} value={totals.missing} label="Missing mandatory" />
          </div>

          <StaffWorkspace mode="compliance" onOpenStaff={onOpenStaff} />

          <SoftCard pad="p-0" hover={false}>
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800 dark:text-gray-100">
                Documents needing attention
              </h2>
              <span className="text-xs text-gray-400">
                Watched continuously · 90 / 60 / 30 / 7-day alerts
              </span>
            </div>
            {attention.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-gray-500">
                Everything is current. Nothing expiring or missing.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {attention.slice(0, 12).map(({ doc, staff, health }) => {
                  const badge = HEALTH_BADGE[health];
                  return (
                    <li
                      key={doc.id}
                      className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                      onClick={() => onOpenStaff(staff.id)}
                    >
                      <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-[var(--accent-link-dark,#93c5fd)] flex items-center justify-center text-xs font-bold shrink-0">
                        {staffInitials(staff)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                          {staffFullName(staff)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {DOC_TYPE_LABELS[doc.docType]}
                          {doc.referenceNumber ? ` · ref ${doc.referenceNumber}` : ''}
                        </p>
                      </div>
                      <span className="text-xs text-gray-400 hidden sm:block whitespace-nowrap">
                        {health === 'missing' ? 'mandatory' : expiryNote(doc)}
                      </span>
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-lg whitespace-nowrap ${badge.cls}`}>
                        {badge.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </SoftCard>

          <details className="group">
            <summary className="cursor-pointer font-semibold text-gray-700 dark:text-gray-200 select-none">
              Per-staff health overview
            </summary>
            <div className="mt-3">
            <div className="flex items-center justify-between mb-3 gap-3">
              <h2 className="font-semibold text-gray-800 dark:text-gray-100">
                Per-staff health
              </h2>
              <div className="relative w-full max-w-xs">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search staff…"
                  className={glassFieldClass('pl-9 py-2')}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredStaff.map(({ staff, sum }) => {
                const badge = HEALTH_BADGE[sum.worst];
                return (
                  <button
                    key={staff.id}
                    onClick={() => onOpenStaff(staff.id)}
                    className="text-left"
                  >
                    <SoftCard pad="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-[var(--accent-link-dark,#93c5fd)] flex items-center justify-center text-sm font-bold shrink-0">
                          {staffInitials(staff)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                            {staffFullName(staff)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {STAFF_CATEGORY_LABELS[staff.category]}
                          </p>
                        </div>
                        <span className={`text-xs font-medium px-2 py-1 rounded-lg shrink-0 ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </div>
                    </SoftCard>
                  </button>
                );
              })}
            </div>
            </div>
          </details>
        </>
      )}
    </div>
  );
}
